import { Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes } from "lodash";
import React from "react";
import BSInput from "../../../common/js/components/BSInput";
import ContactInformation from "../contact/ContactContainer";
import DocumentDrop from "../documents/DocumentDropContainer";
import DocumentPropertyManagementSnapshotContainer from "../documents/DocumentPropertyManagementSnapshotContainer";
import AccountingForm from "./AccountingForm";
import ManagementFees from "./ManagementFees";
import MasterPropertyForm from "./MasterPropertyForm";
import PropertyManagementInformation from "./PropertyManagementInformation";
import PropertyManagementInformationSnapshot from "./PropertyManagementInformationSnapshot";

const DividerComments = styled(Divider)({
    marginBottom: "20px",
});

const DividerMasterPropertyInformation = styled(Divider)({
    marginBottom: "10px",
});

const AssetPortfolioHeader = styled("p")({
    fontSize: "14px",
    fontWeight: "bold",
});

const PropertyManagementComponent = (props) => {
    const {
        role,
        snapshotContacts,
        snapshotMediaAssets,
        snapshotPersonnel,
        property,
        propertyManagement,
        autocompleteResults,
        searchKMPerson,
        handleInput,
        handleAddArrayElement,
        handleDeleteArrayElement,
        handleUpdateArrayElement,
        prop_management_contact_options,
        handleSearchCompany,
        errors,
        isSnapshot,
        hasPortfolioProperties,
    } = props;

    return (
        <React.Fragment>
            <div style={{ display: "flex" }}>
                <div style={{ flex: "1 1 auto" }}>
                    <h3 style={{ marginBottom: "16px" }}> Master Property Information</h3>
                </div>
                {hasPortfolioProperties && (
                    <div>
                        <AssetPortfolioHeader>
                            Linked Properties
                            <br />
                            (by Accounting Software ID)
                        </AssetPortfolioHeader>
                    </div>
                )}
            </div>
            <MasterPropertyForm
                {...props}
                data={propertyManagement}
                disableInputs={!includes(["transition", "dataAdmin", "dataEntry"], role) || isSnapshot}
                handleInput={
                    isSnapshot ? () => {} : (field, value) => handleInput("propertyManagement." + field, value)
                }
                hasPortfolioProperties={hasPortfolioProperties}
                isSnapshot={isSnapshot}
            />

            {isSnapshot ? (
                <PropertyManagementInformationSnapshot snapshotPersonnel={snapshotPersonnel} />
            ) : (
                <PropertyManagementInformation
                    data={propertyManagement}
                    autocompleteResults={autocompleteResults}
                    searchKMPerson={searchKMPerson}
                    handleInput={(field, value) => handleInput("propertyManagement." + field, value)}
                    handleAddArrayElement={(path) => handleAddArrayElement("propertyManagement." + path)}
                    handleDeleteArrayElement={(path, index) =>
                        handleDeleteArrayElement("propertyManagement." + path, index)
                    }
                    handleUpdateArrayElement={(path, index, field, value) =>
                        handleUpdateArrayElement("propertyManagement." + path, index, field, value)
                    }
                    errors={errors}
                    disableInputs={!includes(["transition", "dataAdmin", "dataEntry"], role)}
                />
            )}

            <DividerMasterPropertyInformation />

            <div className="container">
                <div className="row" style={{ marginBottom: "16px" }}>
                    <div className="col-12">
                        <ContactInformation
                            {...props}
                            handleChange={
                                isSnapshot
                                    ? () => {}
                                    : (field, value, index) =>
                                          handleInput(`propertyManagement.contacts[${index}].${field}`, value)
                            }
                            handleAdd={() => handleAddArrayElement("propertyManagement.contacts")}
                            handleDelete={(index) => handleDeleteArrayElement("propertyManagement.contacts", index)}
                            contacts={isSnapshot ? snapshotContacts : get(propertyManagement, "contacts", [])}
                            contactTypeOptions={prop_management_contact_options}
                            addButtonText="Contact Information"
                            includeInvoiceRecipientCheckbox={false}
                            maxContacts={5}
                            displayWhenEmpty={false}
                            companyFieldAvailable={true}
                            errors={{}}
                            handleSearchCompany={handleSearchCompany}
                            disableInputs={!includes(["transition", "dataAdmin", "dataEntry"], role) || isSnapshot}
                            isSnapshot={isSnapshot}
                        />
                    </div>
                </div>
            </div>

            <Divider />

            <div className="container">
                <div className="row" style={{ marginBottom: "16px" }}>
                    <div className="col">
                        <h3>Accounting</h3>
                    </div>
                </div>
            </div>

            <AccountingForm
                {...props}
                data={propertyManagement}
                handleInput={
                    isSnapshot ? () => {} : (field, value) => handleInput("propertyManagement." + field, value)
                }
                sideBySide={true}
                errors={errors}
                disableInputs={!includes(["transition", "dataAdmin", "dataEntry"], role) || isSnapshot}
            />

            <Divider />

            <div className="container">
                <div className="row" style={{ marginBottom: "16px" }}>
                    <div className="col">
                        <h3>Management Fees</h3>
                    </div>
                </div>
            </div>

            <ManagementFees
                {...props}
                data={propertyManagement}
                handleInput={
                    isSnapshot ? () => {} : (field, value) => handleInput("propertyManagement." + field, value)
                }
                sideBySide={true}
                disableInputs={!includes(["transition", "dataAdmin", "dataEntry"], role) || isSnapshot}
                isSnapshot={isSnapshot}
            />

            <DividerComments />

            <div className="container">
                <div className="row" style={{ marginBottom: "16px" }}>
                    <div className="col">
                        <h3>Comments</h3>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="row" style={{ marginBottom: "16px" }}>
                    <div className="col">
                        <BSInput
                            id="comments"
                            placeholder="Enter comments"
                            value={get(propertyManagement, "comments", "")}
                            onChange={
                                isSnapshot
                                    ? () => {}
                                    : (e) => handleInput("propertyManagement.comments", e.target.value)
                            }
                            disabled={!includes(["transition", "dataAdmin", "dataEntry"], role) || isSnapshot}
                            multiline
                            rows={10}
                        />
                    </div>
                </div>
            </div>

            <Divider />

            <div className="container">
                <div className="row" style={{ marginBottom: "16px" }}>
                    <div className="col">
                        <h3>Attached Documents</h3>
                    </div>
                </div>
            </div>
            {isSnapshot ? (
                <DocumentPropertyManagementSnapshotContainer snapshotMediaAssets={snapshotMediaAssets} />
            ) : (
                <DocumentDrop
                    entityKey={get(propertyManagement, "propertyManagementKey", null)}
                    type="PROPERTY_MANAGEMENT"
                    documentTypes="propertyManagement"
                    viewOnly={!includes(["transition", "dataAdmin", "dataEntry"], role) || isSnapshot}
                    parentType={"PROPERTY_MANAGEMENT"}
                    stateCode={get(property, "primaryAddress.city.state.stateCode", null)}
                    noHeader={true}
                    isSnapshot={isSnapshot}
                />
            )}
        </React.Fragment>
    );
};

export default PropertyManagementComponent;
